import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
  Plus,
  Edit2,
  Trash2,
  List,
  Phone,
  Mail,
  MessageSquare,
  CheckSquare,
  ArrowRight,
  Users,
  UserPlus,
  Eye,
  Zap,
} from 'react-feather';
import {
  CollectionSequenceCreate,
  CollectionSequenceStep,
  CollectionSequenceWithUser,
  TaskType,
} from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getSupplierCollectionSequences,
  createCollectionSequence,
  updateCollectionSequence,
  deleteCollectionSequence,
  enrollCollectionSequenceEnrollment,
} from '../../../../redux/services/collectionSequenceService';
import { getSupplierCollectionTriggers } from '../../../../redux/services/collectionTriggerService';
import { getSupplierSummary } from '../../../../redux/services/suppliersService';
import { getSupplierBuyers } from '../../../../redux/services/supplierBuyersService';
import { buildSupplierPath } from '../../../../utils/helpers';
import { DillButton, DillToggle } from '../../../general/Dill';
import { CollectionSequenceFormModal } from './CollectionSequenceFormModal';
import { EnrollCustomersModal } from './EnrollCustomersModal';
import { useCollectionBuyerEngagements } from './useCollectionBuyerEngagements';

const TASK_TYPE_ICONS: Record<TaskType, typeof Phone> = {
  CALL: Phone,
  EMAIL: Mail,
  TEXT_MESSAGE: MessageSquare,
  TO_DO: CheckSquare,
};

const StepBadge = ({ step, index }: { step: CollectionSequenceStep; index: number }) => {
  const Icon = TASK_TYPE_ICONS[step.taskType];
  return (
    <span className="inline-flex items-center gap-1">
      {index > 0 && <ArrowRight size={10} className="text-gray-300 shrink-0" />}
      <span className="inline-flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5 text-[11px] text-gray-600 whitespace-nowrap">
        <Icon size={11} />
        {index === 0 ? 'Now' : `+${step.delayDays}d`}
      </span>
    </span>
  );
};

interface CollectionSequencesTabProps {
  supplierId: string;
}

export const CollectionSequencesTab = ({ supplierId }: CollectionSequencesTabProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { sequences, loadingList } = useAppSelector((state) => state.collectionSequences);
  const { triggers } = useAppSelector((state) => state.collectionTriggers);
  const { supplierUsers } = useAppSelector((state) => state.supplier);
  const { supplierBuyers } = useAppSelector((state) => state.supplierBuyers);
  const { isBuyerAvailable, refresh: refreshEngagements } =
    useCollectionBuyerEngagements(supplierId);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingSequence, setEditingSequence] = useState<CollectionSequenceWithUser | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [enrollModalSequence, setEnrollModalSequence] = useState<CollectionSequenceWithUser | null>(
    null
  );
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollErrorMessage, setEnrollErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (supplierId) {
      dispatch(getSupplierCollectionSequences({ supplierId }));
      // Also needed here (not just on the Triggers tab) so the "Enroll customers" picker can
      // cross-check against every trigger's targetBuyerIds directly - see CollectionSequenceFormModal.
      dispatch(getSupplierCollectionTriggers({ supplierId }));
    }
  }, [supplierId, dispatch]);

  useEffect(() => {
    if (supplierId && supplierUsers.length === 0) {
      dispatch(getSupplierSummary({ supplierId }));
    }
  }, [supplierId, supplierUsers.length, dispatch]);

  useEffect(() => {
    if (supplierId && supplierBuyers.length === 0) {
      dispatch(getSupplierBuyers({ supplierId }));
    }
  }, [supplierId, supplierBuyers.length, dispatch]);

  const isLoading = loadingList.includes('getSupplierCollectionSequences');

  const handleCreate = () => {
    setEditingSequence(null);
    setErrorMessage(null);
    setIsFormModalOpen(true);
    refreshEngagements();
  };

  const handleEdit = (sequence: CollectionSequenceWithUser) => {
    setEditingSequence(sequence);
    setErrorMessage(null);
    setIsFormModalOpen(true);
    refreshEngagements();
  };

  const handleClose = () => {
    setIsFormModalOpen(false);
    setEditingSequence(null);
    setErrorMessage(null);
  };

  const handleSubmit = async (data: CollectionSequenceCreate) => {
    if (!supplierId) return;
    setErrorMessage(null);
    const result = editingSequence
      ? await dispatch(
          updateCollectionSequence({ supplierId, sequenceId: editingSequence.id, data })
        )
      : await dispatch(createCollectionSequence({ supplierId, data: { ...data, supplierId } }));
    const payload = result.payload as { success?: boolean } | string | null;
    const failed = !payload || (typeof payload === 'object' && payload.success === false);
    if (failed) {
      setErrorMessage('Failed to save collection sequence. Please try again.');
      return;
    }
    handleClose();
    dispatch(getSupplierCollectionSequences({ supplierId }));
    refreshEngagements();
  };

  const handleToggleActive = async (sequence: CollectionSequenceWithUser) => {
    await dispatch(
      updateCollectionSequence({
        supplierId,
        sequenceId: sequence.id,
        data: { isActive: !sequence.isActive },
      })
    );
    dispatch(getSupplierCollectionSequences({ supplierId }));
  };

  const handleDelete = async (sequence: CollectionSequenceWithUser) => {
    if ((sequence.triggerCount ?? 0) > 0) return;
    await dispatch(deleteCollectionSequence({ supplierId, sequenceId: sequence.id }));
    dispatch(getSupplierCollectionSequences({ supplierId }));
  };

  const buyerOptions = supplierBuyers
    .filter((b) => isBuyerAvailable(b.id))
    .map((b) => ({ label: b.name || 'Unnamed customer', value: b.id }));
  const unavailableBuyerCount = supplierBuyers.length - buyerOptions.length;

  const handleOpenEnrollModal = (sequence: CollectionSequenceWithUser) => {
    setEnrollErrorMessage(null);
    setEnrollModalSequence(sequence);
    refreshEngagements();
  };

  const handleCloseEnrollModal = () => {
    setEnrollModalSequence(null);
    setEnrollErrorMessage(null);
  };

  const handleEnrollSubmit = async (buyerIds: string[]) => {
    if (!enrollModalSequence || buyerIds.length === 0) return;
    setEnrollErrorMessage(null);
    setIsEnrolling(true);
    const results = await Promise.all(
      buyerIds.map((buyerId) =>
        dispatch(
          enrollCollectionSequenceEnrollment({
            supplierId,
            sequenceId: enrollModalSequence.id,
            buyerId,
          })
        )
      )
    );
    setIsEnrolling(false);

    const failedCount = results.filter((result) => {
      const payload = result.payload as { success?: boolean } | null;
      return !payload || payload.success === false;
    }).length;

    if (failedCount > 0) {
      setEnrollErrorMessage(
        failedCount === buyerIds.length
          ? 'Failed to enroll. Please try again.'
          : `Enrolled ${buyerIds.length - failedCount} of ${
              buyerIds.length
            } - some customers could not be enrolled.`
      );
    }
    if (failedCount < buyerIds.length) {
      dispatch(getSupplierCollectionSequences({ supplierId }));
      refreshEngagements();
    }
    if (failedCount === 0) {
      handleCloseEnrollModal();
    }
  };

  return (
    <div className="w-full h-full pt-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
            <List size={18} />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 m-0">Collection Sequences</p>
            <p className="text-xs text-gray-400 mt-0.5 m-0">
              The steps that run once a customer is enrolled - directly, or via a Collection
              Trigger.
            </p>
          </div>
        </div>
        <DillButton
          label="New sequence"
          icon={<Plus size={14} />}
          onClick={handleCreate}
          height={31}
        />
      </div>

      {isLoading && sequences.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-8">Loading collection sequences…</p>
      )}

      {!isLoading && sequences.length === 0 && (
        <div className="bg-white border border-dashed border-gray-200 rounded-xl py-10 flex flex-col items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-300 flex items-center justify-center">
            <List size={18} />
          </div>
          <p className="text-sm text-gray-500 m-0 mt-1">No collection sequences yet</p>
          <p className="text-xs text-gray-400 m-0">
            Create one to define what happens once a customer is enrolled, e.g. "call now, email 3
            days later, text 5 days later".
          </p>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        {sequences.map((sequence) => {
          const sortedSteps = [...sequence.steps].sort((a, b) => a.order - b.order);
          const inUse = (sequence.triggerCount ?? 0) > 0;

          return (
            <div
              key={sequence.id}
              onClick={() =>
                navigate(
                  buildSupplierPath(supplierId, `/collectionSequencesAndTriggers/${sequence.id}`)
                )
              }
              className="group bg-white border border-gray-100 rounded-xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-indigo-50 text-indigo-600">
                <List size={18} />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold text-gray-900 m-0 truncate">
                    {sequence.name}
                  </p>
                  {!sequence.isActive && (
                    <span className="text-[10px] font-medium text-gray-400 bg-gray-100 rounded-full px-2 py-0.5 shrink-0">
                      Paused
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5 shrink-0">
                    <Users size={10} />
                    {sequence.buyerIds.length} customer{sequence.buyerIds.length === 1 ? '' : 's'}
                  </span>
                  {inUse && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-600 bg-indigo-50 rounded-full px-2 py-0.5 shrink-0">
                      <Zap size={10} />
                      Used by {sequence.triggerCount} trigger
                      {sequence.triggerCount === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {sortedSteps.map((step, index) => (
                    <StepBadge key={step.id} step={step} index={index} />
                  ))}
                </div>
              </div>

              <div
                className="flex items-center gap-2 shrink-0 pl-3 border-l border-gray-100"
                onClick={(e) => e.stopPropagation()}>
                <span data-tooltip-id={`toggle-tooltip-${sequence.id}`}>
                  <DillToggle
                    value={sequence.isActive}
                    onChange={() => handleToggleActive(sequence)}
                  />
                </span>
                <ReactTooltip
                  id={`toggle-tooltip-${sequence.id}`}
                  place="top"
                  variant="dark"
                  opacity="100%"
                  style={{ borderRadius: '8px', fontSize: '12px', zIndex: 10000 }}>
                  {sequence.isActive
                    ? 'Active — new customers can be enrolled and steps keep running'
                    : 'Paused — no new steps run, but existing enrollments are unaffected'}
                </ReactTooltip>

                <DillButton
                  variant="secondary"
                  label="Enroll"
                  icon={<UserPlus size={12} />}
                  height={28}
                  onClick={() => handleOpenEnrollModal(sequence)}
                />
                <DillButton
                  variant="secondary"
                  label="Details"
                  icon={<Eye size={12} />}
                  height={28}
                  onClick={() =>
                    navigate(
                      buildSupplierPath(
                        supplierId,
                        `/collectionSequencesAndTriggers/${sequence.id}`
                      )
                    )
                  }
                />
                <span data-tooltip-id={`edit-tooltip-${sequence.id}`}>
                  <DillButton
                    variant="icon-default"
                    icon={<Edit2 size={12} />}
                    height={28}
                    width={28}
                    onClick={() => handleEdit(sequence)}
                  />
                </span>
                <ReactTooltip
                  id={`edit-tooltip-${sequence.id}`}
                  place="top"
                  variant="dark"
                  opacity="100%"
                  style={{ borderRadius: '8px', fontSize: '12px', zIndex: 10000 }}>
                  Edit
                </ReactTooltip>

                <span data-tooltip-id={`delete-tooltip-${sequence.id}`}>
                  <DillButton
                    variant="icon-red"
                    icon={<Trash2 size={12} />}
                    height={28}
                    width={28}
                    isDisabled={inUse}
                    onClick={() => handleDelete(sequence)}
                  />
                </span>
                <ReactTooltip
                  id={`delete-tooltip-${sequence.id}`}
                  place="top"
                  variant="dark"
                  opacity="100%"
                  style={{
                    borderRadius: '8px',
                    fontSize: '12px',
                    maxWidth: '220px',
                    zIndex: 10000,
                  }}>
                  {inUse ? 'Detach it from every trigger using it first' : 'Delete'}
                </ReactTooltip>
              </div>
            </div>
          );
        })}
      </div>

      <CollectionSequenceFormModal
        open={isFormModalOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
        initialData={editingSequence}
        isLoading={
          loadingList.includes('createCollectionSequence') ||
          loadingList.includes('updateCollectionSequence')
        }
        errorMessage={errorMessage}
        supplierUsers={supplierUsers}
        supplierBuyers={supplierBuyers}
        triggers={triggers}
        isBuyerAvailable={isBuyerAvailable}
      />

      <EnrollCustomersModal
        open={!!enrollModalSequence}
        onClose={handleCloseEnrollModal}
        onSubmit={handleEnrollSubmit}
        buyerOptions={buyerOptions}
        unavailableCount={unavailableBuyerCount}
        isSubmitting={isEnrolling}
        errorMessage={enrollErrorMessage}
      />
    </div>
  );
};

export default CollectionSequencesTab;
